// React-side locale state, backed by paraglide-js's localStorage strategy.
//
// Paraglide already persists the choice in localStorage and re-renders
// messages on next read. We need ONE more thing: tell React to re-render
// when the locale changes. We do that with a tiny pub/sub.
//
// Why we DON'T use `overwriteSetLocale`:
//   The previous design wrapped paraglide's `setLocale` so every caller
//   (including paraglide's internal strategy init) would notify our
//   subscribers. The wrapper called a captured snapshot of the original
//   to avoid recursing on the `export let setLocale` live binding.
//   That worked in pure native ESM, but Vite's dep optimizer pre-bundles
//   the paraglide runtime into a chunk-XXX.js that resolves named imports
//   through a getter shim — so the "snapshot" wasn't stable and the
//   wrapper called itself, blowing the stack the moment the user clicked
//   the EN ↔ PT toggle.
//
// New design: don't touch paraglide. Export OUR OWN setter that calls
// `paraglideSetLocale` directly + notifies subscribers. The only caller
// of locale changes in the app is `LocaleToggle` (routes/root.tsx), and
// it uses the hook below. Paraglide's internal `setLocale(resolved,
// {reload:false})` during first-read strategy init still happens, but
// it doesn't change the user-visible locale value — so missing a notify
// for that one call is fine.
//
// Source of truth ordering:
//   1. paraglide's localStorage value (PARAGLIDE_LOCALE) — set explicitly.
//   2. baseLocale ('en') if nothing stored.
//
// Server-side preference (user.preferred_locale) is NOT yet persisted —
// when that lands, /me will include it and we'll seed setLocale on signin.

import { useSyncExternalStore } from 'react';
import {
  type Locale,
  getLocale,
  locales,
  setLocale as paraglideSetLocale,
} from './paraglide/runtime';

/** All supported locales — `('en' | 'pt')[]`. */
export type AppLocale = (typeof locales)[number];

const listeners = new Set<() => void>();

function notify() {
  for (const fn of listeners) fn();
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function getSnapshot(): AppLocale {
  return getLocale() as AppLocale;
}

/**
 * App-level locale setter. Calls paraglide directly (no wrapper, no
 * overwriteSetLocale) and notifies React subscribers so components
 * holding `useLocale()` re-render.
 */
function appSetLocale(next: AppLocale): void {
  paraglideSetLocale(next as Locale, { reload: false });
  notify();
}

/** React hook returning the current locale + a setter. */
export function useLocale(): [AppLocale, (locale: AppLocale) => void] {
  const locale = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return [locale, appSetLocale];
}

/** Read-only locale getter for non-React code (e.g. fetch helpers). */
export function currentLocale(): AppLocale {
  return getLocale() as AppLocale;
}

export const supportedLocales = locales;
